// src/context/AuthContext.js

import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribeUser = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      // Önceki Firestore listener'ını temizle
      if (unsubscribeUser) {
        unsubscribeUser();
        unsubscribeUser = null;
      }

      if (user) {
        // Kullanıcı giriş yaptıysa Firestore'dan rol bilgisini al
        const userDocRef = doc(db, 'users', user.uid);
        unsubscribeUser = onSnapshot(userDocRef, (docSnapshot) => {
          if (docSnapshot.exists()) {
            const userData = docSnapshot.data();
            setCurrentUser({
              uid: user.uid,
              email: user.email,
              displayName: user.displayName,
              role: userData.role || 'Servis Teknisyeni' // Varsayılan rol
            });
          } else {
            // Kullanıcı dokümanı yoksa varsayılan değerlerle ayarla
            setCurrentUser({
              uid: user.uid,
              email: user.email,
              displayName: user.displayName,
              role: 'Servis Teknisyeni'
            });
          }
          setLoading(false);
        }, (error) => {
          // Permission hatası normal (kullanıcı çıkış yapmış olabilir)
          if (error.code !== 'permission-denied') {
            console.error("Kullanıcı bilgileri alınırken hata:", error);
          }
          // Hata durumunda bile kullanıcı bilgilerini ayarla (varsayılan rol ile)
          if (user) {
            setCurrentUser({
              uid: user.uid,
              email: user.email,
              displayName: user.displayName,
              role: 'Servis Teknisyeni'
            });
          }
          setLoading(false);
        });
      } else {
        // Kullanıcı çıkış yaptı
        setCurrentUser(null);
        setLoading(false);
      }
    });

    // Cleanup: Hem auth hem de Firestore listener'larını temizle
    return () => {
      if (unsubscribeUser) {
        unsubscribeUser();
      }
      unsubscribeAuth();
    };
  }, []);

  const value = {
    currentUser,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
